/**
 * Los campos personalizados de una oferta: «Ingredientes», «Cómo usar»…
 *
 * La página de cada oferta se diseña UNA vez por tipo en el editor, así que lo
 * que cambia de un producto a otro tiene que ser un dato del producto. Los
 * campos son del tipo (todos los productos tienen los mismos) y cada oferta
 * guarda lo suyo. Ver `OfferFields` en @webbuilder/schemas.
 *
 * Por el patrón de las fichas («la ficha enseña, el asistente edita»), aquí
 * está el paso del asistente, donde se rellenan y se crean; las fichas los
 * enseñan como una fila más de su resumen, que abre este paso.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconPhoto, IconPlus, IconTrash, IconX } from '@tabler/icons-react'
import { BlockEditor, BlockEditorEnvProvider } from '@webbuilder/ui'
import { MAX_OFFER_FIELDS, offerFieldIdFor, type OfferCustomField, type OfferCustomFieldType, type OfferFields, type OfferTemplateKind } from '@webbuilder/schemas'
import { getOfferFields, saveOfferFields } from '../../../lib/api'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { useBlockEditorEnv } from './BlogPostEditor'

/** El tipo de plantilla de una oferta: el mismo reparto que el catálogo. */
export function offerTemplateKindOf(offer: { type: string; bookingDurationMinutes?: number | null | undefined }): OfferTemplateKind {
  if (offer.bookingDurationMinutes !== null && offer.bookingDurationMinutes !== undefined) return 'bookings'
  if (offer.type === 'physical') return 'products'
  if (offer.type === 'service') return 'services'
  return 'digital'
}

/** Los campos del proyecto, con su guardado. Una lectura por pantalla. */
export function useOfferFields(projectId: string, token: string) {
  const [fields, setFields] = useState<OfferFields | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let alive = true
    getOfferFields(projectId, token)
      .then((data) => { if (alive) setFields(data.fields) })
      .catch(() => { if (alive) { setFields({}); setFailed(true) } })
    return () => { alive = false }
  }, [projectId, token])
  const save = useCallback(async (kind: OfferTemplateKind, list: OfferCustomField[]) => {
    const data = await saveOfferFields(projectId, kind, list, token)
    setFields(data.fields)
  }, [projectId, token])
  return { fields, failed, save }
}

/** El paso del asistente: un control por campo y, al pie, crear otro. */
export function OfferCustomFieldsEditor({ projectId, token, kind, values, onChange }: {
  projectId: string
  token: string
  kind: OfferTemplateKind
  values: Record<string, string>
  onChange: (values: Record<string, string>) => void
}) {
  const { t } = useI18n()
  const { fields: all, failed, save } = useOfferFields(projectId, token)
  const env = useBlockEditorEnv(token, projectId)
  const fields = all?.[kind] ?? []
  const [adding, setAdding] = useState(false)
  const [label, setLabel] = useState('')
  const [type, setType] = useState<OfferCustomFieldType>('richText')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [picking, setPicking] = useState<string | null>(null)
  const set = (id: string, value: string) => onChange({ ...values, [id]: value })

  async function addField() {
    const name = label.trim()
    if (!name || busy) return
    setBusy(true)
    setError('')
    try {
      const id = offerFieldIdFor(name, fields.map((field) => field.id))
      await save(kind, [...fields, { id, label: name, type }])
      setAdding(false)
      setLabel('')
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
    } finally {
      setBusy(false)
    }
  }

  /* Quitar un campo lo quita de TODAS las ofertas de este tipo: se pregunta. */
  async function removeField(field: OfferCustomField) {
    if (!window.confirm(t('offerFields.removeConfirm', { name: field.label }))) return
    setBusy(true)
    try {
      await save(kind, fields.filter((entry) => entry.id !== field.id))
      const { [field.id]: _removed, ...rest } = values
      onChange(rest)
    } catch (cause) {
      setError(translateServerError(cause, t('error.unexpected')))
    } finally {
      setBusy(false)
    }
  }

  if (!all) return <p className="dash-offer-card-foot">{t('offerFields.loading')}</p>

  return (
    <div className="dash-cfields">
      {failed ? <small className="dash-svc-field-error">{t('offerFields.loadFailed')}</small> : null}
      {fields.length === 0 && !adding ? <p className="dash-cfields-empty">{t('offerFields.empty')}</p> : null}

      {fields.map((field) => (
        <div key={field.id} className="dash-svc-field dash-cfield">
          <span className="dash-cfield-head">
            {field.label}
            <button type="button" className="dash-cfield-remove" title={t('offerFields.remove')} aria-label={t('offerFields.removeOf', { name: field.label })} disabled={busy} onClick={() => { void removeField(field) }}>
              <IconTrash size={13} />
            </button>
          </span>
          {field.type === 'text' ? (
            <input type="text" maxLength={300} value={values[field.id] ?? ''} onChange={(event) => set(field.id, event.target.value)} />
          ) : field.type === 'image' ? (
            <span className="dash-cfield-image">
              {values[field.id] ? (
                <>
                  <img src={values[field.id]} alt="" />
                  <button type="button" className="dash-secondary-btn" onClick={() => setPicking(field.id)}>{t('offerFields.imageChange')}</button>
                  <button type="button" className="dash-icon-btn" aria-label={t('offerFields.imageRemove')} onClick={() => set(field.id, '')}><IconX size={14} /></button>
                </>
              ) : (
                <button type="button" className="dash-secondary-btn" onClick={() => setPicking(field.id)}><IconPhoto size={14} /> {t('offerFields.imagePick')}</button>
              )}
            </span>
          ) : (
            <div className="dash-block-editor-host dash-cfield-rich">
              <BlockEditorEnvProvider value={env}>
                <BlockEditor value={values[field.id] ?? ''} onCommit={(html) => set(field.id, html)} />
              </BlockEditorEnvProvider>
            </div>
          )}
        </div>
      ))}

      {adding ? (
        <div className="dash-cfield-new">
          <label className="dash-svc-field">
            <span>{t('offerFields.newName')}</span>
            <input autoFocus type="text" maxLength={60} placeholder={t('offerFields.newPlaceholder')} value={label}
              onChange={(event) => setLabel(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void addField() } }} />
          </label>
          <label className="dash-svc-field">
            <span>{t('offerFields.newType')}</span>
            <select value={type} onChange={(event) => setType(event.target.value as OfferCustomFieldType)}>
              <option value="richText">{t('offerFields.type.richText')}</option>
              <option value="text">{t('offerFields.type.text')}</option>
              <option value="image">{t('offerFields.type.image')}</option>
            </select>
          </label>
          <small>{t('offerFields.newHint')}</small>
          <span className="dash-cfield-new-actions">
            <button type="button" className="dash-secondary-btn" onClick={() => { setAdding(false); setLabel('') }}>{t('common.cancel')}</button>
            <button type="button" className="dash-primary-btn" disabled={!label.trim() || busy} onClick={() => { void addField() }}>{t('offerFields.create')}</button>
          </span>
        </div>
      ) : fields.length < MAX_OFFER_FIELDS ? (
        <button type="button" className="dash-secondary-btn dash-cfield-add" onClick={() => setAdding(true)}>
          <IconPlus size={14} /> {t('offerFields.add')}
        </button>
      ) : null}
      {error ? <small className="dash-svc-field-error" role="alert">{error}</small> : null}

      {picking ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onSelect={(url) => { set(picking, url); setPicking(null) }}
          onClose={() => setPicking(null)}
        />
      ) : null}
    </div>
  )
}
